import React, { useEffect, useState } from 'react';
import { useT } from '../../../i18n/LanguageProvider';
import { engagementService } from '../../../services/request/engagement';

interface Props {
    cardId: string;
    recipientEmail: string;
    readOnly?: boolean;
}

const EMOJIS = ['❤️', '👍', '👏', '🎉', '😊', '🙏'];

const getLocalKey = (cardId: string) => `reactions_${cardId}`;

const ReactionBar: React.FC<Props> = ({ cardId, recipientEmail, readOnly }) => {
    const { t } = useT();
    const [grouped, setGrouped] = useState<{ [emoji: string]: number }>({});
    const [myReactions, setMyReactions] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState<string | null>(null);

    const loadInitialMyReactions = (cardId: string) => {
        try {
            const stored = localStorage.getItem(getLocalKey(cardId));
            return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
        } catch {
            return new Set<string>();
        }
    };

    const persistMyReactions = (cardId: string, reactions: Set<string>) => {
        localStorage.setItem(getLocalKey(cardId), JSON.stringify(Array.from(reactions)));
    };

    useEffect(() => {
        setMyReactions(loadInitialMyReactions(cardId));
        engagementService
            .getReactions(cardId)
            .then((data) => setGrouped(data?.grouped || {}))
            .catch(() => { });
    }, [cardId]);

    const handleClick = async (emoji: string) => {
        if (loading || readOnly) return;
        setLoading(emoji);
        const isSelected = myReactions.has(emoji);
        try {
            if (isSelected) {
                await engagementService.removeReaction(cardId, recipientEmail, emoji);
                setGrouped((prev) => ({ ...prev, [emoji]: Math.max((prev[emoji] || 1) - 1, 0) }));
                const next = new Set(myReactions);
                next.delete(emoji);
                setMyReactions(next);
                persistMyReactions(cardId, next);
            } else {
                await engagementService.addReaction(cardId, recipientEmail, emoji);
                setGrouped((prev) => ({ ...prev, [emoji]: (prev[emoji] || 0) + 1 }));
                const next = new Set(myReactions);
                next.add(emoji);
                setMyReactions(next);
                persistMyReactions(cardId, next);
            }
        } catch (err) {
            console.error('Reaction error:', err);
        } finally {
            setLoading(null);
        }
    };

    return (
        <div className="mt-6 px-5 py-4 bg-white bg-opacity-85 backdrop-blur-sm rounded-2xl shadow-md w-full max-w-lg">
            <p className="text-xs font-semibold text-slate-500 mb-2.5 tracking-widest uppercase">
                {t('viewcard.reactToThisCard')}
            </p>
            <div className="flex flex-wrap gap-2">
                {EMOJIS.map((emoji) => {
                    const selected = myReactions.has(emoji);
                    const count = grouped[emoji] || 0;
                    return (
                        <button
                            key={emoji}
                            onClick={() => handleClick(emoji)}
                            disabled={loading === emoji || readOnly}
                            title={readOnly ? 'Preview mode' : selected ? 'Remove reaction' : 'Add reaction'}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border-2 cursor-pointer transition-all duration-150
                                ${selected
                                    ? 'bg-indigo-100 border-indigo-500'
                                    : 'bg-slate-50 border-slate-200'
                                }
                                ${loading === emoji ? 'scale-95' : 'scale-100'}
                            `}
                        >
                            <span className="text-lg leading-none">{emoji}</span>
                            {count > 0 && (
                                <span className="text-xs font-semibold text-indigo-600">{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ReactionBar;
